import type { ProductOfferingDTO, ProductOfferingPriceDTO } from '@t/api.types';
import type { CategoryType, Channel, Plan, PlanAddOn, PlanDiscount } from '@t/productOffering.types';
import { getCmsImageUrl } from '@utils/cmsImageUrl';

const getRecurringOfferingPrice = (prices: ProductOfferingPriceDTO[] | undefined): ProductOfferingPriceDTO | undefined =>
  prices?.find(
    (p) =>
      p.priceType === 'recurringCharge' ||
      (p.priceType !== 'discount' &&
        p.priceType !== 'oneTime' &&
        p.priceType !== 'oneTimeCharge' &&
        p.recurringChargePeriodType &&
        p.recurringChargePeriodType !== 'oneTime'),
  );

const KNOWN_CATEGORIES: ReadonlySet<string> = new Set(['Internet', 'Video', 'Phone']);

/** Shape of the JSON-encoded description returned by the API. */
interface DescriptionJson {
  summary?: string;
  highlights?: string[];
  moreDetails?: string[];
  billCycle?: string;
  channelList?: string[];
  includedFeatures?: string[];
  discountTag?: string;
  discountMessage?: string;
  channelDetailsImage?: string;
  callingUsagePolicy?: string;
  callingScope?: string;
  filterName?: string;
  quantity?: string;
  image?: string;
}

/**
 * Safely parses a description string as JSON.
 * Returns null if the string is not valid JSON or not an object.
 */
export const parseDescriptionJson = (description: string | undefined): DescriptionJson | null => {
  if (!description) return null;
  try {
    const parsed = JSON.parse(description);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    return parsed as DescriptionJson;
  } catch {
    return null;
  }
};

/**
 * Converts ##text## bold markers in highlight strings to **text** markdown.
 */
const convertBoldMarkers = (text: string): string => text.replace(/##(.+?)##/g, '**$1**');

/**
 * Converts an array of channel code strings to Channel objects with CMS logo URLs.
 */
const toChannelList = (names: string[] | undefined): Channel[] | undefined => {
  if (!names || names.length === 0) return undefined;
  return names.map((name) => ({
    name,
    logo: getCmsImageUrl('channel-logo', name) ?? '',
    descriptionImage: getCmsImageUrl('channel-logo-desc', name),
  }));
};

/**
 * Maps a list of child ProductOfferingDTOs to PlanAddOn objects (optional / non-mandatory children only).
 */
const mapChildrenToAddOns = (children: ProductOfferingDTO[]): PlanAddOn[] =>
  children
    .filter((child) => !child.isMandatory)
    .map((child) => {
      const childDesc = parseDescriptionJson(child.description);
      const childRecurringPrice = getRecurringOfferingPrice(child.productOfferingPrice);
      // A price entry that exists but carries no numeric amount is malformed/missing data from the
      // catalog, not a genuine $0 add-on — flag it so the UI can show "unavailable" instead of $0.
      const childPriceUnavailable = childRecurringPrice != null && childRecurringPrice.price?.amount == null;
      return {
        id: child.id,
        name: child.name,
        description: childDesc?.summary ?? (childDesc ? '' : (child.description ?? '')),
        rawDescription: child.description,
        code: child.code ?? '',
        price: childRecurringPrice?.price?.amount ?? 0,
        priceUnavailable: childPriceUnavailable,
        currency: childRecurringPrice?.price?.currency ?? 'CAD',
        isMandatory: child.isMandatory ?? false,
        category:
          child.category?.[0] || childDesc?.filterName
            ? {
                id: child.category?.[0]?.id ?? '',
                name: childDesc?.filterName ?? child.category?.[0]?.name ?? '',
                description: child.category?.[0]?.description,
              }
            : undefined,
        imageUrl: getCmsImageUrl('channel-logo', childDesc?.image ?? child.code),
        descriptionImage: getCmsImageUrl('channel-logo-desc', childDesc?.image ?? child.code),
        channels: toChannelList(childDesc?.channelList),
        discountTag: childDesc?.discountTag,
        discountMessage: childDesc?.discountMessage,
        supportsQuantity: childDesc?.quantity === 'true',
      };
    });

/**
 * Extracts the default value for a named characteristic from a ProductOfferingDTO.
 */
const getCharacteristicValue = (dto: ProductOfferingDTO, name: string): unknown => {
  const char = dto.productOfferingCharacteristic?.find((c) => c.name === name);
  const spec = char?.characteristicValueSpecification?.find((s) => s.isDefault);
  return spec?.value;
};

/**
 * Maps a single top-level ProductOfferingDTO to an internal Plan model.
 * Returns null if the offering doesn't map to a known category.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * TMF catalog offering IDs are UUIDs; GLDS catalog IDs are numeric. There's no dedicated
 * "is TMF" flag on the shared ProductOfferingDTO, so the ID shape is the only signal available.
 */
export const isTmfOfferingId = (id: string | undefined): boolean => !!id && UUID_RE.test(id);

const mapBundleOfferingToPlan = (dto: ProductOfferingDTO): Plan | null => {
  const mandatoryMembers = (dto.childItems ?? []).filter((c) => c.isMandatory);
  if (mandatoryMembers.length === 0) return null;
  // TMF catalog uses UUID IDs; GLDS uses numeric IDs — skip non-TMF bundles
  if (!mandatoryMembers.some((m) => isTmfOfferingId(m.id))) return null;

  // Derive price as the sum of each member's recurring price. If any member has a price entry
  // with no usable amount, the sum is unreliable — flag it rather than silently under-charging.
  const memberRecurringPrices = mandatoryMembers.map(
    (member) =>
      member.productOfferingPrice?.find((p) => p.priceType === 'recurring') ??
      member.productOfferingPrice?.[0]
  );
  const bundlePriceUnavailable = memberRecurringPrices.some(
    (memberRecurring) => memberRecurring != null && memberRecurring.price?.amount == null
  );
  const price = memberRecurringPrices.reduce((sum, memberRecurring) => sum + (memberRecurring?.price?.amount ?? 0), 0);
  const currency = mandatoryMembers[0]?.productOfferingPrice?.[0]?.price?.currency ?? 'CAD';

  const bundledMemberIds = mandatoryMembers.map((m) => m.id).filter((id): id is string => !!id);

  // Detect which mandatory member has optional add-ons (non-mandatory, non-equipment children)
  const addOnMember = mandatoryMembers.find((m) =>
    (m.childItems ?? []).some((c) => !c.isMandatory && c.type !== 'equipment')
  );
  const addOnMemberCategory = addOnMember
    ? (getCharacteristicValue(addOnMember, 'typeName') as CategoryType | undefined)
    : undefined;

  const desc = parseDescriptionJson(dto.description);

  return {
    planId: dto.id,
    name: dto.name,
    price,
    priceUnavailable: bundlePriceUnavailable,
    currency,
    type: 'bundle',
    typeName: 'Bundle',
    typeOrder: 10,
    isBundle: true,
    isTopOffer: dto.isTopOffer ?? false,
    bundledMemberIds,
    bundleAddOnMemberId: addOnMember?.id,
    bundleAddOnMemberCategory: addOnMemberCategory,
    bundleAddOnMemberAddOns: addOnMember ? mapChildrenToAddOns(addOnMember.childItems ?? []) : undefined,
    bundleRelationships: [],
    bundledMembers: [],
    addOns: [],
    discounts: [],
    description: {
      summary: desc?.summary ?? (desc ? '' : (dto.description ?? '')),
      highlights: desc?.highlights ?? [],
      benefits: [],
      moreDetails: desc?.moreDetails ?? [],
      billCycle: desc?.billCycle ?? 'month',
      discountTag: desc?.discountTag,
      discountMessage: desc?.discountMessage,
    },
  };
};

const mapOfferingToPlan = (dto: ProductOfferingDTO): Plan | null => {
  const typeName = getCharacteristicValue(dto, 'typeName') as string | undefined;
  if (!typeName || !KNOWN_CATEGORIES.has(typeName)) {
    if (dto.isBundle && (dto.childItems ?? []).some((c) => c.isMandatory)) {
      return mapBundleOfferingToPlan(dto);
    }
    return null;
  }

  const typeCode = String(getCharacteristicValue(dto, 'type') ?? '');
  const typeOrder = Number(getCharacteristicValue(dto, 'typeOrder') ?? 0);

  // Price: prefer recurring, fall back to first non-discount (e.g. one-time fees come last)
  const isOneTimePrice = (priceType: string | undefined) =>
    priceType === 'oneTime' || priceType === 'oneTimeCharge';
  const basePrice =
    dto.productOfferingPrice?.find((p) => p.priceType === 'recurring') ??
    dto.productOfferingPrice?.find((p) => p.priceType !== 'discount' && !isOneTimePrice(p.priceType));
  // A matched price entry with no usable amount is malformed/missing catalog data, not a genuine
  // $0 plan — flag it so the UI can show "unavailable" rather than laundering it into a free price.
  const priceUnavailable = basePrice != null && basePrice.price?.amount == null;
  const price = basePrice?.price?.amount ?? 0;
  const currency = basePrice?.price?.currency ?? 'CAD';

  // One-time fee (e.g. installation charge)
  const oneTimePriceEntry = (dto.productOfferingPrice ?? []).find((p) => isOneTimePrice(p.priceType));
  const oneTimeFee = oneTimePriceEntry?.price?.amount;

  // Discounts
  const discounts: PlanDiscount[] = (dto.productOfferingPrice ?? [])
    .filter((p) => p.priceType === 'discount')
    .map((p) => ({
      id: p.id,
      name: p.name,
      amount: p.price?.amount ?? 0,
      currency: p.price?.currency ?? 'CAD',
      priceType: p.priceType ?? 'discount',
      recurringChargePeriodType: p.recurringChargePeriodType,
    }));

  // Child items → add-ons (optional) and mandatory highlights
  const children = dto.childItems ?? [];
  const addOns = mapChildrenToAddOns(children);

  // Build discount display strings from description JSON (preferred) or from price discounts
  const desc = parseDescriptionJson(dto.description);
  const discountTag = desc?.discountTag ?? (discounts.length > 0 ? 'Promotion' : undefined);
  const discountMessage = desc?.discountMessage ?? discounts[0]?.name ?? undefined;

  // Build mandatory child item text as highlights
  const mandatoryHighlights = children
    .filter((c) => c.isMandatory && c.description)
    .map((c) => c.description!)
    .filter((d) => d.length > 0 && !d.startsWith('{'));

  // Parse description JSON for rich plan details
  const summary = desc?.summary ?? (desc ? '' : (dto.description ?? ''));
  const highlights = (desc?.highlights ?? []).map(convertBoldMarkers);

  return {
    planId: dto.id,
    name: dto.name,
    price,
    priceUnavailable,
    currency,
    type: typeCode,
    typeName: typeName as CategoryType,
    typeOrder,
    isBundle: dto.isBundle ?? false,
    isTopOffer: dto.isTopOffer ?? false,
    addOns,
    discounts,
    oneTimeFee,
    bundleRelationships: [],
    bundledMembers: [],
    description: {
      summary,
      highlights: [...highlights, ...mandatoryHighlights],
      benefits: [],
      moreDetails: desc?.moreDetails ?? [],
      billCycle: desc?.billCycle ?? 'month',
      discountTag,
      discountMessage,
      channelList: toChannelList(desc?.channelList),
      channelDetailsImage: desc?.channelDetailsImage,
      callingUsagePolicy: desc?.callingUsagePolicy,
      callingScope: desc?.callingScope,
      includedFeatures: desc?.includedFeatures,
    },
  };
};

/**
 * Maps an array of ProductOfferingDTO (raw API response) into Plan objects,
 * filtering out non-plan offerings (e.g. TV Addons, equipment).
 * Results are sorted by typeOrder then by price.
 */
export const mapOfferingsToPlan = (dtos: ProductOfferingDTO[]): Plan[] => {
  const topOffers = dtos.filter((dto) => dto.isTopOffer);
  const plans = topOffers
    .map(mapOfferingToPlan)
    .filter((plan): plan is Plan => plan !== null)
    .sort((a, b) => a.typeOrder - b.typeOrder || a.price - b.price);
  return plans;
};

/**
 * Maps EV product offerings from the TMF catalog into Plan objects.
 * All returned offerings are treated as top-level EV plans — no isTopOffer filter,
 * since the catalog URL already scopes to the EV category.
 */
const readProdSpecCharValue = (dto: ProductOfferingDTO, name: string): string | undefined => {
  const char = dto.prodSpecCharValueUse?.find((c) => c.name === name);
  const val = char?.productSpecCharacteristicValue?.find((v) => v.isDefault)?.value
    ?? char?.productSpecCharacteristicValue?.[0]?.value;
  return val != null ? String(val) : undefined;
};

export const mapEvOfferingsToPlan = (dtos: ProductOfferingDTO[]): Plan[] =>
  dtos.map((dto) => {
    const desc = parseDescriptionJson(dto.description);
    const serviceType = readProdSpecCharValue(dto, 'serviceType') ?? 'EV';
    const recurringPrice = dto.productOfferingPrice?.find((p) => p.priceType === 'recurring');
    const oneTimePrice = dto.productOfferingPrice?.find((p) => p.priceType === 'oneTime');
    const displayPrice = recurringPrice ?? oneTimePrice ?? dto.productOfferingPrice?.[0];
    const price = displayPrice?.price?.amount ?? 0;
    const priceType = displayPrice?.priceType;
    const currency = displayPrice?.price?.currency ?? 'CAD';

    const discounts: PlanDiscount[] = (dto.productOfferingPrice ?? [])
      .filter((p) => p.priceType === 'discount')
      .map((p) => ({
        id: p.id,
        name: p.name,
        amount: p.price?.amount ?? 0,
        currency: p.price?.currency ?? 'CAD',
        priceType: p.priceType ?? 'discount',
        recurringChargePeriodType: p.recurringChargePeriodType,
      }));

    return {
      planId: dto.id,
      name: dto.name,
      price,
      priceType,
      currency,
      type: 'ev',
      typeName: serviceType as 'EV',
      typeOrder: 99,
      isBundle: false,
      isTopOffer: true,
      addOns: [],
      discounts,
      bundleRelationships: (dto.productOfferingRelationship ?? [])
        .filter((r) => !!r.id)
        .map((r) => ({ id: r.id, name: r.name ?? '', href: r.href ?? '' })),
      bundledMembers: (dto.bundledProductOffering ?? [])
        .filter((m) => !!m.id)
        .map((m) => ({ id: m.id, name: m.name ?? '', href: m.href ?? '' })),
      description: {
        summary: desc?.summary ?? (desc ? '' : (dto.description ?? '')),
        highlights: (desc?.highlights ?? []),
        benefits: [],
        moreDetails: desc?.moreDetails ?? [],
        billCycle: desc?.billCycle ?? 'month',
        discountTag: desc?.discountTag,
        discountMessage: desc?.discountMessage,
      },
    };
  })
  .sort((a, b) => a.price - b.price);
